import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import './ResetPassword.css';

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    label: string;
    color: string;
  } | null>(null);

  useEffect(() => {
    if (!token) {
      setMessage('Invalid reset link. Please request a new password reset.');
      setIsValidating(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`/api/password-reset/validate/${token}`);
      const data = await response.json();

      if (data.success && data.data.valid) {
        setIsValidToken(true);
        setUserInfo(data.data.user);
      } else {
        setMessage('This password reset link is invalid or has expired. Please request a new one.');
      }
    } catch (error) {
      setMessage('Failed to validate reset link. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const checkPasswordStrength = async (password: string) => {
    if (!password || password.length === 0) {
      setPasswordStrength(null);
      return;
    }

    try {
      const response = await fetch('/api/auth/check-password-strength', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();
      if (data.success) {
        const strength = data.data.strength;
        let label = 'Weak';
        let color = '#e74c3c';
        
        if (strength >= 80) {
          label = 'Very Strong';
          color = '#27ae60';
        } else if (strength >= 60) {
          label = 'Strong';
          color = '#2ecc71';
        } else if (strength >= 40) {
          label = 'Fair';
          color = '#f39c12';
        } else if (strength >= 20) {
          label = 'Weak';
          color = '#e67e22';
        } else {
          label = 'Very Weak';
          color = '#e74c3c';
        }

        setPasswordStrength({ score: strength, label, color });
      }
    } catch (error) {
      console.error('Failed to check password strength:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Check password strength if it's the password field
    if (name === 'newPassword') {
      checkPasswordStrength(value);
    }
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { newPassword?: string; confirmPassword?: string } = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 10) {
      newErrors.newPassword = 'Password must be at least 10 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first invalid field
      const firstErrorField = Object.keys(errors).find(key => errors[key as keyof typeof errors]);
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest' 
          });
          element.focus();
        }
      }
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/password-reset/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: formData.newPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        setMessage(data.message);
      } else {
        // If there are detailed validation errors, show them
        if (data.details && Array.isArray(data.details) && data.details.length > 0) {
          setMessage(`${data.error}\n${data.details.join('\n')}`);
        } else {
          setMessage(data.error || 'Failed to reset password');
        }
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (isValidating) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Validating reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="error-state">
            <AlertCircle size={48} className="error-icon" />
            <h2>Invalid Reset Link</h2>
            <p>{message}</p>
            <button onClick={handleBackToLogin} className="btn-primary btn-auto" style={{marginTop: '1.5rem'}} >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="success-state">
            <CheckCircle size={48} className="success-icon" />
            <h2>Password Reset Successful!</h2>
            <p>{message}</p>
            <p>
              Your password has been updated for <strong>{userInfo?.email}</strong>
            </p>
            <button onClick={handleBackToLogin} className="btn-primary btn-auto" style={{marginTop: '1.5rem'}}>
              Sign In with New Password
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <div className="header-content">
            {/* <div className="icon-container">
              <Lock size={32} className="lock-icon" />
            </div> */}
            <h1 className="title">Reset Your Password</h1>
            <p className="subtitle">
              Enter your new password below.
            </p>
            {userInfo && (
              <p className="user-email">
                Resetting password for: <strong>{userInfo.email}</strong>
              </p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              New Password
            </label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter your new password"
                className={`form-input ${errors.newPassword ? 'error' : ''}`}
                required
                disabled={isLoading}
                minLength={10}
                aria-describedby={errors.newPassword ? 'newPassword-error' : undefined}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {passwordStrength && (
              <div className="password-strength" style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.875rem', color: '#666' }}>Password Strength</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '500', color: passwordStrength.color }}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div style={{ 
                  width: '100%', 
                  height: '6px', 
                  backgroundColor: '#e0e0e0', 
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    width: `${passwordStrength.score}%`, 
                    height: '100%', 
                    backgroundColor: passwordStrength.color,
                    transition: 'all 0.3s ease'
                  }}></div>
                </div>
              </div>
            )}
            <div className="password-requirements">
              <p>Password Requirements:</p>
              <ul>
                <li className={formData.newPassword.length >= 10 ? 'met' : 'unmet'}>
                  10 character minimum
                </li>
                <li className={/[A-Z]/.test(formData.newPassword) ? 'met' : 'unmet'}>
                  Must contain uppercase (A-Z)
                </li>
                <li className={/[a-z]/.test(formData.newPassword) ? 'met' : 'unmet'}>
                  Must contain lowercase (a-z)
                </li>
                <li className={/\d/.test(formData.newPassword) ? 'met' : 'unmet'}>
                  Must contain number (0-9)
                </li>
                <li className={/[@$!%*?&#^()_+\-=\[\]{}|;:,.<>\/]/.test(formData.newPassword) ? 'met' : 'unmet'}>
                  Must contain special character from: @$!%*?&#^()_+-={'[]{}'}|;:,.&lt;&gt;/
                </li>
                <li className={!/(.)\1{3,}/.test(formData.newPassword) ? 'met' : 'unmet'}>
                  Cannot have more than 3 repeating characters in a row
                </li>
              </ul>
            </div>
            {errors.newPassword && (
              <div id="newPassword-error" className="error-message" role="alert">
                {errors.newPassword}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm New Password
            </label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your new password"
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                required
                disabled={isLoading}
                aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <div id="confirmPassword-error" className="error-message" role="alert">
                {errors.confirmPassword}
              </div>
            )}
          </div>

          {message && (
            <div className={`message ${isSuccess ? 'success' : 'error'}`}>
              {isSuccess ? (
                <CheckCircle size={20} className="message-icon" />
              ) : (
                <AlertCircle size={20} className="message-icon" />
              )}
              <span>{message}</span>
            </div>
          )}

          <button
            type="submit"
            className="btn-primary btn-full"
            style={{marginTop: '1.5rem'}}
            disabled={isLoading || !formData.newPassword || !formData.confirmPassword}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="reset-password-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login" className="login-link">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
